import React, { useMemo, useState } from 'react';
import DealCard from './DealCard';

// Pipeline stages configuration
const stages = [
    { id: 'new', label: 'New Inquiry', color: '#4338ca' },
    { id: 'qualified', label: 'Qualified', color: '#1d4ed8' },
    { id: 'proposal', label: 'Proposal', color: '#b45309' },
    { id: 'site_visit', label: 'Site Visit', color: '#f97316' },
    { id: 'negotiation', label: 'Negotiation / Booking', color: '#c2410c' },
    { id: 'won', label: 'Closed Won', color: '#15803d' },
    { id: 'lost', label: 'Closed Lost', color: '#b91c1c' },
];

// Format currency as abbreviated Indian Rupees
function formatAbbreviatedCurrency(value) {
    if (!value && value !== 0) return '₹0';

    if (value >= 10000000) {
        return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
        return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
        return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
}

function DealPipeline({ pipeline, onStageChange, onDealClick, stageMetrics, movingDealId = null, isStageUpdating = false, movingTargetStage = null }) {
    const [draggedDeal, setDraggedDeal] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);

    const handleDragStart = (e, deal) => {
        if (isStageUpdating) {
            e.preventDefault();
            return;
        }
        setDraggedDeal(deal);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, stageId) => {
        if (isStageUpdating) return;
        e.preventDefault();
        setDragOverStage(stageId);
    };

    const handleDragLeave = () => {
        setDragOverStage(null);
    };

    const handleDrop = (e, targetStage) => {
        if (isStageUpdating) return;
        e.preventDefault();
        setDragOverStage(null);

        if (draggedDeal && draggedDeal.stage !== targetStage) {
            onStageChange?.(draggedDeal.id, targetStage);
        }
        setDraggedDeal(null);
    };

    const handleDragEnd = () => {
        setDraggedDeal(null);
        setDragOverStage(null);
    };

    // Show loading spinner if pipeline is null
    if (!pipeline) {
        return (
            <div className="crm-loading">
                <div className="crm-spinner"></div>
                <span className="crm-loading-text">Loading pipeline...</span>
            </div>
        );
    }

    const computedMetrics = useMemo(() => {
        if (stageMetrics) return stageMetrics;
        return {};
    }, [stageMetrics]);

    return (
        <div className={`crm-pipeline ${isStageUpdating ? 'crm-pipeline-updating' : ''}`}>
            {stages.map((stage) => {
                const stageData = pipeline[stage.id] || { deals: [], count: 0, value: 0 };
                const deals = stageData.deals || [];
                const isDropTarget = dragOverStage === stage.id;
                const isMoveTarget = isStageUpdating && movingTargetStage === stage.id;
                const metrics = computedMetrics[stage.id] || {};

                return (
                    <div
                        key={stage.id}
                        className="crm-pipeline-column"
                        data-stage={stage.id}
                        onDragOver={(e) => handleDragOver(e, stage.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, stage.id)}
                        style={{
                            background: (isDropTarget || isMoveTarget) ? 'rgba(59, 130, 246, 0.1)' : undefined,
                        }}
                    >
                        <div
                            className="crm-pipeline-header"
                            style={{ borderBottomColor: stage.color }}
                        >
                            <div className="crm-pipeline-title">
                                {stage.label}
                                <span className="crm-pipeline-count">{stageData.count || deals.length}</span>
                            </div>
                            <div className="crm-pipeline-stats">
                                {formatAbbreviatedCurrency(stageData.value || 0)}
                                {metrics.overdueCount > 0 && (
                                    <span style={{ marginLeft: '8px', color: 'var(--crm-danger)' }}>
                                        {metrics.overdueCount} overdue
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="crm-pipeline-cards">
                            {deals.length > 0 ? (
                                deals.map((deal) => (
                                    <DealCard
                                        key={deal.id}
                                        deal={deal}
                                        onDragStart={handleDragStart}
                                        onClick={onDealClick}
                                        isDragging={draggedDeal?.id === deal.id}
                                        isBusy={movingDealId === deal.id}
                                        disableDrag={isStageUpdating}
                                    />
                                ))
                            ) : (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '24px',
                                        color: 'var(--crm-gray-400)',
                                        fontSize: '14px',
                                    }}
                                >
                                    No deals
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {isStageUpdating && (
                <div className="crm-pipeline-busy-overlay" aria-live="polite">
                    <span className="crm-spinner crm-spinner-inline" />
                    <span>Updating deal stage...</span>
                </div>
            )}

            {/* Invisible drag end handler */}
            <div
                style={{ display: 'none' }}
                onDragEnd={handleDragEnd}
            />
        </div>
    );
}

export default DealPipeline;
